/**
 * PendingSuggestionsPage — the redesigned web Review surface.
 *
 * Replaces ProfileReviewQueue's flat list with:
 *   • FilterChips at top (All / Blocks / Follows / Boosts / Skips)
 *   • Groups in fixed order Blocks → Follows → Boosts → Skips, each
 *     section labeled with its sub-line ("Highest stakes — apply or
 *     snooze", etc.).
 *   • Docked EvidenceSheet aside on the right at >=md breakpoint.
 *     Below md, EvidenceSheet renders itself as a fullScreen Dialog
 *     (it owns its own responsive container — see PR 2).
 *
 * Translated from design/pages/desktop/PendingSuggestions.jsx. The
 * vocabulary (group sub-lines, "See N videos" disclosure, ordering)
 * is the source of truth; keep this aligned if design refreshes.
 */
import React, {useMemo, useState, useEffect} from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {useParams} from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  Stack,
  Typography,
} from '@mui/material';
import BlockOutlined from '@mui/icons-material/BlockOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import SkipNextOutlined from '@mui/icons-material/SkipNextOutlined';
import InboxOutlined from '@mui/icons-material/InboxOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {getAuth} from 'firebase/auth';
import {
  Suggestion,
  SuggestionVerb,
  Author,
  Video,
} from '../../types/generated/graphql';
import {
  useGetPendingSuggestions,
  useApplySuggestion,
  useSnoozeSuggestion,
  useFlagSuggestionWrong,
} from '../../api/graphql_suggestions';
import {track} from '../../lib/telemetry';
import {SuggestionRow} from './SuggestionRow';
import {
  FilterChips,
  SuggestionFilter,
  tokenForFilter,
  countByVerb,
} from './FilterChips';
import {
  EvidenceSheet,
  EvidenceSheetData,
  EvidenceSheetPersona,
} from './EvidenceSheet';
import {VERB_TONES} from './EvidenceSheet/tones';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

interface GroupDef {
  verb: SuggestionVerb;
  label: string;
  sub: string;
  Icon: typeof BlockOutlined;
}

// Fixed display order. The redesign brief is explicit: Blocks first
// (highest stakes), then Follows / Boosts / Skips.
const GROUPS: GroupDef[] = [
  {
    verb: SuggestionVerb.Block,
    label: 'Blocks',
    sub: 'Highest stakes — apply or snooze',
    Icon: BlockOutlined,
  },
  {
    verb: SuggestionVerb.Follow,
    label: 'Follows',
    sub: "Channels he loves that YouTube isn't serving",
    Icon: CheckCircleOutline,
  },
  {
    verb: SuggestionVerb.Boost,
    label: 'Boosts',
    sub: 'Adjacent channels worth a 30-day try',
    Icon: TrendingUpOutlined,
  },
  {
    verb: SuggestionVerb.Skip,
    label: 'Skips',
    sub: 'Borderline — dismiss without action',
    Icon: SkipNextOutlined,
  },
];

function useIsAdmin(): boolean {
  // Mirrors the firebase-auth claim check used by ProfileHome's
  // ProfileStrip — read role from the ID token result. No Redux
  // slice exists for this in the repo today (the brief mentions
  // one, but the real codebase uses firebase claims directly).
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    const user = getAuth().currentUser;
    if (!user) return;
    let cancelled = false;
    user
      .getIdTokenResult()
      .then(t => {
        if (!cancelled) setIsAdmin(t.claims.role === 'admin');
      })
      .catch(err => console.warn('[PendingSuggestionsPage] admin claim check failed:', err));
    return () => {
      cancelled = true;
    };
  }, []);
  return isAdmin;
}

function targetName(target: Suggestion['target']): string {
  if (target && 'title' in (target as Record<string, unknown>)) {
    return (target as Video).title ?? 'Untitled video';
  }
  return (target as Author)?.name ?? 'Channel';
}

function buildEvidenceData(s: Suggestion): EvidenceSheetData {
  const isVideo = s.target && 'title' in (s.target as Record<string, unknown>);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const t = s.target as any;
  const thumbnailUrl: string | null =
    t?.thumbnails?.[0]?.url ?? t?.author?.thumbnails?.[0]?.url ?? null;
  return {
    verb: s.verb,
    target: {
      kind: isVideo ? 'video' : 'channel',
      name: targetName(s.target),
      thumbnailUrl,
    },
    evidence: s.evidence,
  };
}

function EmptyEvidence(): React.JSX.Element {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        height: '100%',
        backgroundColor: '#FFFFFF',
        p: 5,
        textAlign: 'center',
      }}
    >
      <InboxOutlined sx={{fontSize: 40, color: '#334155', mb: 1.5}} />
      <Typography sx={{fontWeight: 500, color: '#334155', fontSize: '14px'}}>
        Pick a suggestion to see the evidence
      </Typography>
      <Typography sx={{mt: 0.5, maxWidth: 280, color: '#64748B', fontSize: '12px', lineHeight: 1.5}}>
        You&apos;ll see the videos that triggered it, what changes if you apply, and (for admins) the
        scoring chain.
      </Typography>
    </Stack>
  );
}

export default function PendingSuggestionsPage({client}: Props): React.JSX.Element {
  const {pid} = useParams<{pid: string}>();
  const profileId = pid as string;
  const isAdmin = useIsAdmin();
  const persona: EvidenceSheetPersona = isAdmin ? 'admin' : 'parent';

  const [filter, setFilter] = useState<SuggestionFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const {data, loading, error} = useGetPendingSuggestions(
    client,
    profileId,
    tokenForFilter(filter),
  );
  const [applySuggestion, {loading: applying}] = useApplySuggestion(client);
  const [snoozeSuggestion, {loading: snoozing}] = useSnoozeSuggestion(client);
  const [flagSuggestionWrong] = useFlagSuggestionWrong(client);

  const suggestions = useMemo(() => data?.pendingSuggestions ?? [], [data]);
  const counts = useMemo(() => countByVerb(suggestions), [suggestions]);
  const byVerb = useMemo(() => {
    const map = new Map<SuggestionVerb, Suggestion[]>();
    for (const s of suggestions) {
      const list = map.get(s.verb) ?? [];
      list.push(s);
      map.set(s.verb, list);
    }
    return map;
  }, [suggestions]);

  const selected = useMemo(
    () => suggestions.find(s => s.id === selectedId) ?? null,
    [suggestions, selectedId],
  );

  return (
    <Box sx={{minHeight: '100vh', backgroundColor: '#F8FAFC', color: '#0F172A'}}>
      {/* Single outer Container matching ProfileHome.tsx — header,
          filter chips, and body all live inside it. No full-bleed
          white strips: the slate-50 page background shows on either
          side, same horizontal rhythm as the /profile/:pid view. */}
      <Container maxWidth="md" sx={{px: 3, py: 2}}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 2}}>
          {/* Back affordance lives in the ProfileLayout's breadcrumb
              ("← back to {name}") above — render the page's own
              "REVIEW / Pending suggestions" title only, no duplicate
              back button. */}
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                textTransform: 'uppercase',
                color: '#64748B',
                fontSize: '10px',
                letterSpacing: '0.1em',
              }}
            >
              Review
            </Typography>
            <Typography
              component="h1"
              sx={{fontWeight: 600, color: '#0F172A', fontSize: '18px', lineHeight: 1.2}}
            >
              Pending suggestions
            </Typography>
          </Box>
          {persona === 'admin' && (
            <Box
              sx={{
                border: '1px solid #C7D2FE',
                backgroundColor: '#EEF2FF',
                color: '#4338CA',
                px: 1.25,
                py: 0.5,
                borderRadius: '999px',
                fontSize: '10px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Admin
            </Box>
          )}
        </Stack>

        <Box sx={{mb: 2.5}}>
          <FilterChips value={filter} onChange={setFilter} counts={counts} />
        </Box>
        <Box sx={{overflowY: 'auto'}}>
          <Stack spacing={4}>
            {loading && suggestions.length === 0 && (
              <Stack alignItems="center" sx={{py: 6}}>
                <CircularProgress size={24} sx={{color: '#94A3B8'}} />
              </Stack>
            )}

            {error && (
              <Box
                sx={{
                  borderRadius: '12px',
                  border: '1px solid rgba(244, 63, 94, 0.4)',
                  backgroundColor: 'rgba(244, 63, 94, 0.05)',
                  p: 2,
                }}
              >
                <Typography sx={{color: '#BE123C', fontSize: '13px'}}>
                  Couldn&apos;t load suggestions: {error.message}
                </Typography>
              </Box>
            )}

            {GROUPS.map(g => {
              const rows = byVerb.get(g.verb) ?? [];
              if (rows.length === 0) return null;
              const tones = VERB_TONES[g.verb.toLowerCase() as 'block' | 'follow' | 'boost' | 'skip'];
              return (
                <Box component="section" key={g.verb}>
                  <Stack
                    direction="row"
                    alignItems="baseline"
                    justifyContent="space-between"
                    sx={{mb: 1.5}}
                  >
                    <Stack direction="row" alignItems="baseline" spacing={1}>
                      <g.Icon sx={{fontSize: 14, color: tones.accent}} />
                      <Typography
                        component="h2"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          color: '#334155',
                          fontSize: '11px',
                          letterSpacing: '0.1em',
                        }}
                      >
                        {g.label}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: '#F1F5F9',
                          color: '#94A3B8',
                          borderRadius: '4px',
                          px: '6px',
                          fontWeight: 600,
                          fontSize: '10px',
                          lineHeight: '16px',
                        }}
                      >
                        {rows.length}
                      </Box>
                    </Stack>
                    <Typography sx={{color: '#64748B', fontSize: '11px'}}>{g.sub}</Typography>
                  </Stack>
                  <Stack spacing={1.25}>
                    {rows.map(row => (
                      <SuggestionRow
                        key={row.id}
                        suggestion={row}
                        selected={row.id === selectedId}
                        onSelect={() => {
                          setSelectedId(row.id);
                          track('suggestion_viewed', {id: row.id, verb: row.verb, persona});
                        }}
                        onApply={() => {
                          track('suggestion_applied', {
                            id: row.id,
                            verb: row.verb,
                            source: 'row_button',
                          });
                          applySuggestion({variables: {suggestionId: row.id}}).catch(err =>
                            console.warn('[PendingSuggestionsPage] apply failed:', err),
                          );
                          if (selectedId === row.id) setSelectedId(null);
                        }}
                        onSnooze={() => {
                          track('suggestion_snoozed', {id: row.id, verb: row.verb, days: 7});
                          snoozeSuggestion({variables: {suggestionId: row.id, days: 7}}).catch(err =>
                            console.warn('[PendingSuggestionsPage] snooze failed:', err),
                          );
                          if (selectedId === row.id) setSelectedId(null);
                        }}
                        applying={applying}
                        snoozing={snoozing}
                      />
                    ))}
                  </Stack>
                </Box>
              );
            })}

            {!loading && suggestions.length === 0 && (
              <Box
                sx={{
                  borderRadius: '12px',
                  border: '1px dashed #CBD5E1',
                  p: 5,
                  textAlign: 'center',
                }}
              >
                <CheckCircleOutline sx={{fontSize: 32, color: '#34D399', mb: 1}} />
                <Typography sx={{fontWeight: 500, color: '#0F172A', fontSize: '14px'}}>
                  Nothing pending right now.
                </Typography>
                <Typography sx={{mt: 0.5, color: '#64748B', fontSize: '12px'}}>
                  Suggestions appear here when the model finds something worth your input.
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
                backgroundColor: '#FFFFFF',
                p: 2,
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <InfoOutlined sx={{fontSize: 16, color: '#64748B', flexShrink: 0, mt: '2px'}} />
                <Typography sx={{color: '#94A3B8', fontSize: '12px', lineHeight: 1.55}}>
                  Looking for your past votes or things that actually happened on YouTube? Both live
                  in <Box component="span" sx={{color: '#0F172A', fontWeight: 600}}>Activity</Box> — this page is only
                  suggestions waiting on your input.
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Box>

      </Container>

      <Dialog
        open={!!selected}
        onClose={() => setSelectedId(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{sx: {borderRadius: 2}}}
      >
        {selected && (
          <EvidenceSheet
            data={buildEvidenceData(selected)}
            persona={persona}
            suggestionId={selected.id}
            onClose={() => setSelectedId(null)}
            onApply={() => {
              track('suggestion_applied', {
                id: selected.id,
                verb: selected.verb,
                source: 'sheet_primary',
              });
              applySuggestion({variables: {suggestionId: selected.id}}).catch(err =>
                console.warn('[PendingSuggestionsPage] apply failed:', err),
              );
              setSelectedId(null);
            }}
            onSnooze={() => {
              track('suggestion_snoozed', {
                id: selected.id,
                verb: selected.verb,
                days: 7,
              });
              snoozeSuggestion({variables: {suggestionId: selected.id, days: 7}}).catch(err =>
                console.warn('[PendingSuggestionsPage] snooze failed:', err),
              );
              setSelectedId(null);
            }}
            onFlagWrong={(suggestionId, verb) => {
              // Admin-only — the EvidenceSheet only wires this prop
              // for persona='admin'. Telemetry fires regardless of
              // mutation outcome so we can measure intent.
              track('suggestion_flagged_wrong', {
                id: suggestionId,
                verb,
                reason: null,
              });
              flagSuggestionWrong({variables: {suggestionId}}).catch(err =>
                console.warn('[PendingSuggestionsPage] flag failed:', err),
              );
            }}
            forceDocked
          />
        )}
      </Dialog>
    </Box>
  );
}
